import React, { useState, useEffect } from 'react';
import { IMatrixMessage } from '../../../client/message/types';
import { tmsEmcRespondToInvite, tmsEmcGetInviteStatus } from '@app/model';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Avatar from '@mui/material/Avatar';
import { Log, useTranslation } from '@fanfanlo';

interface EmergencyContactInviteMessageProps {
  message: IMatrixMessage;
  className?: string;
}

type InviteStatus = 'pending' | 'accepted' | 'declined' | 'expired' | 'loading' | 'error';

interface InviteData {
  invite_id: string;
  inviter_info: {
    display_name: string;
    avatar_url?: string;
  };
  message?: string;
}

const logger = new Log(false, 'EmergencyContactInviteMessage');

export const EmergencyContactInviteMessage = ({ message, className = '' }: EmergencyContactInviteMessageProps) => {
  const { t } = useTranslation();
  const [status, setStatus] = useState<InviteStatus>('loading');
  const [inviteData, setInviteData] = useState<InviteData | null>(null);
  const [error, setError] = useState<string | null>(null);

  logger.log('Rendering EmergencyContactInviteMessage', {
    messageId: message.event_id,
    content: message.content,
    customContent: message.content.custom?.['app.emergency_contact.invite']
  });

  const inviteId = message.content.custom?.['app.emergency_contact.invite']?.invite_id;

  useEffect(() => {
    logger.log('useEffect triggered', { 
      eventId: message.event_id,
      inviteId,
      hasCustomContent: !!message.content.custom,
      customKeys: message.content.custom ? Object.keys(message.content.custom) : []
    });
    
    const inviteContent = message.content.custom?.['app.emergency_contact.invite'];

    // Log the content for debugging
    logger.log('Invite content:', {
      eventId: message.event_id,
      inviteContent: inviteContent ? {
        ...inviteContent,
        // 避免日志过大，只记录关键信息
        inviter_info: inviteContent.inviter_info ? {
          display_name: inviteContent.inviter_info.display_name,
          has_avatar: !!inviteContent.inviter_info.avatar_url
        } : null
      } : null
    });

    // Ensure we have the necessary data before proceeding
    if (!inviteId) {
      const errorMsg = '无法加载邀请信息: 缺少 invite_id';
      logger.error(errorMsg, { 
        eventId: message.event_id,
        messageContent: message.content,
        inviteContent 
      });
      setError(errorMsg);
      setStatus('error');
      return;
    }

    if (!inviteContent) {
      const errorMsg = '无法加载邀请信息: 缺少邀请内容';
      logger.error(errorMsg, { 
        eventId: message.event_id,
        messageContent: message.content,
        message: message.content.body || 'No message body'
      });
      setError(errorMsg);
      setStatus('error');
      return;
    }

    if (!inviteContent.inviter_info?.display_name) {
      const errorMsg = '无法加载邀请信息: 缺少邀请人信息';
      logger.error(errorMsg, { 
        eventId: message.event_id,
        inviterInfo: inviteContent.inviter_info,
        hasInviterId: !!inviteContent.inviter_id,
        sender: message.sender
      });
      setError(errorMsg);
      setStatus('error');
      return;
    }

    // Set the display data for the invite
    const newInviteData = {
      invite_id: inviteContent.invite_id,
      inviter_info: {
        display_name: inviteContent.inviter_info.display_name,
        avatar_url: inviteContent.inviter_info.avatar_url,
      },
      message: inviteContent.message || inviteContent.invite_message || inviteContent.content,
    };
    
    logger.log('Setting invite data', {
      eventId: message.event_id,
      newInviteData,
      hasMessage: !!newInviteData.message
    });
    
    setInviteData(newInviteData);

    // Define and call the function to fetch the status
    const fetchInviteStatus = async () => {
      logger.log('Fetching invite status', {
        eventId: message.event_id,
        inviteId
      });
      
      setStatus('loading');
      try {
        const response = await tmsEmcGetInviteStatus({
          params: { invite_ids: [inviteId] },
        });

        logger.log('Received invite status response', {
          eventId: message.event_id,
          response: {
            status: response.status,
            data: response.data,
            hasInvites: !!response?.data?.data?.invites?.length
          }
        });

        const invites = response?.data?.data?.invites;

        if (invites && invites.length > 0) {
          const result = invites[0];
          if (result?.status) {
            setStatus(result.status as InviteStatus);
          } else {
            setStatus('expired');
          }
        } else {
          setStatus('expired');
        }
      } catch (error) {
        console.error('[EmergencyContactInviteMessage] 获取邀请状态失败:', error);
        setStatus('error');
        setError(t('获取邀请状态失败，请稍后重试'));
      }
    };

    fetchInviteStatus();
    // By depending on `inviteId`, this effect only re-runs when the invite ID
    // itself changes, preventing repeated calls for the same message.
  }, [inviteId, t]);

  const handleResponse = async (action: 'accepted' | 'declined') => {
    if (!inviteData) return;
    
    setStatus('loading');
    try {
      const res = await tmsEmcRespondToInvite({
        params: { 
          invite_id: inviteData.invite_id, 
          action 
        },
      });
      
      console.log('[EmergencyContactInviteMessage] 响应邀请结果:', res);
      
      if (res?.dataIsOk && res?.shellIsOk) {
        setStatus(action);
      } else {
        throw new Error(res?.data?.msg || '响应邀请失败');
      }
    } catch (err) {
      console.error('[EmergencyContactInviteMessage] 响应邀请失败:', err);
      setStatus('error');
      setError(err instanceof Error ? err.message : '操作失败，请稍后重试');
    }
  };

  if (error) {
    return (
      <Paper 
        elevation={1} 
        sx={{ 
          p: 2, 
          my: 1, 
          borderLeft: '4px solid #f44336',
          backgroundColor: '#fff8f8'
        }}
      >
        <Typography color="error">{error}</Typography>
      </Paper>
    );
  }
  
  if (!inviteData) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', p: 2 }}>
        <CircularProgress size={20} sx={{ mr: 1 }} />
        <Typography>加载邀请信息中...</Typography>
      </Box>
    );
  }
  
  const { inviter_info, message: inviteMessage } = inviteData;

  logger.log('Rendering content with status:', status, { inviteData });

  const renderContent = () => {
    logger.log('Rendering content for status:', status);
    switch (status) {
      case 'accepted':
        return (
          <Typography color="success.main" sx={{ mt: 1, fontStyle: 'italic' }}>
            你已接受此紧急联系人邀请
          </Typography>
        );
      case 'declined':
        return (
          <Typography color="text.secondary" sx={{ mt: 1, fontStyle: 'italic' }}>
            你已拒绝此紧急联系人邀请
          </Typography>
        );
      case 'expired':
        return (
          <Typography color="text.secondary" sx={{ mt: 1, fontStyle: 'italic' }}>
            此邀请已过期
          </Typography>
        );
      case 'error':
        return (
          <Typography color="error" sx={{ mt: 1 }}>
            操作失败，请稍后重试
          </Typography>
        );
      case 'loading':
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
            <CircularProgress size={20} sx={{ mr: 1 }} />
            <Typography>处理中...</Typography>
          </Box>
        );
      case 'pending':
      default:
        return (
          <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
            <Button 
              variant="contained" 
              color="primary"
              onClick={() => handleResponse('accepted')}
              disabled={status === 'loading'}
            >
              接受
            </Button>
            <Button 
              variant="outlined" 
              color="secondary"
              onClick={() => handleResponse('declined')}
              disabled={status === 'loading'}
            >
              拒绝
            </Button>
          </Box>
        );
    }
  };

  return (
    <Paper 
      elevation={0}
      sx={{ 
        p: 2, 
        my: 1,
        borderLeft: '4px solid #1976d2',
        backgroundColor: 'background.paper',
        borderRadius: 1
      }}
      className={className}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
        {inviter_info.avatar_url ? (
          <Avatar 
            src={inviter_info.avatar_url} 
            alt={inviter_info.display_name}
            sx={{ width: 40, height: 40, mr: 1.5 }}
          />
        ) : (
          <Avatar sx={{ width: 40, height: 40, mr: 1.5 }}>
            {inviter_info.display_name.charAt(0).toUpperCase()}
          </Avatar>
        )}
        <Box>
          <Typography variant="subtitle1" fontWeight="medium">
            {inviter_info.display_name}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            邀请你成为紧急联系人
          </Typography>
        </Box>
      </Box>
      
      {inviteMessage && (
        <Box 
          sx={{ 
            p: 1.5, 
            my: 1.5, 
            bgcolor: 'action.hover',
            borderRadius: 1,
            borderLeft: '3px solid',
            borderColor: 'primary.light'
          }}
        >
          <Typography variant="body2" fontStyle="italic">
            "{inviteMessage}"
          </Typography>
        </Box>
      )}
      
      {renderContent()}
    </Paper>
  );
};
